/**
 * The filter bar shared by the pipeline, deals, contacts, companies and
 * activities. `kind` decides which of the optional controls appear.
 *
 * It folds. A row of eight controls sitting permanently above the board
 * pushed the first card below the fold on a laptop, for filters most visits
 * never touch. The bar keeps the Filters button at the right edge, with a
 * count when anything is set, and whatever the page hands in as `tools` -
 * the pipeline's Import and Export - sit beside it, so the things that act on
 * the list are all in one place.
 *
 * Every control here narrows the list below it. That sounds obvious and was
 * not true: the bar offered a Source dropdown on pages whose filter function
 * never read it, so choosing one changed nothing and said nothing about it. A
 * control that cannot work belongs to the page that can use it, and the rest
 * of this file is arranged around which page that is.
 */
import { useState } from 'react';
import { useApp, EMPTY_CRM_FILTERS } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { pad2 } from '../lib/dates.js';
import {
  salesTeam, boardRows, dealsOf, pipelineStages, leadDetails, leadStateLabel,
  filterDeals, picked, MANUAL_SOURCE, IMPORT_SOURCE, segmentOf
} from '../domain/crm.js';
import { Card } from './Primitives.jsx';

const iso = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * The windows people actually ask for, resolved against the clock at click
 * time rather than stored as a mode — so a tab left open overnight cannot go
 * on claiming "today" about yesterday.
 */
const RANGES = [
  ['Today', () => { const n = new Date(); return [n, n]; }],
  ['Last 7 days', () => {
    const n = new Date(); const a = new Date(); a.setDate(a.getDate() - 6); return [a, n];
  }],
  ['Last 30 days', () => {
    const n = new Date(); const a = new Date(); a.setDate(a.getDate() - 29); return [a, n];
  }],
  ['This month', () => {
    const n = new Date();
    return [new Date(n.getFullYear(), n.getMonth(), 1), n];
  }],
  ['Last month', () => {
    const n = new Date();
    return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)];
  }]
];

const ACT_STATES = ['Open', 'Done'];

export default function CrmFilter({ kind, tools = null }) {
  const { S, crm, rows, cF, setCF } = useApp();
  const [open, setOpen] = useState(false);

  const active = Object.entries(cF)
    .filter(([k, v]) => !['from', 'to'].includes(k) && v).length
    + (cF.from || cF.to ? 1 : 0);

  const isBoard = kind === 'pipeline';
  const isDealish = kind === 'deal' || isBoard;

  /* Which rows this bar is narrowing - so the dropdowns offer only values
     those rows actually carry. A Source list read from the settings offered
     "Walk-in" and "Reseller" above a board on which every row said
     "website", and choosing either quietly showed nothing. */
  const pool = isBoard ? boardRows(crm)
    : kind === 'deal' ? dealsOf(crm)
    : kind === 'contact' ? crm.contacts
    : kind === 'company' ? crm.companies
    : [];
  const seen = key => [...new Set(pool.map(d => d[key]).filter(Boolean))].sort();

  /*
   * What the visitor tracker saw, as the values the board actually holds.
   *
   * Offered from the rows rather than from the ten types the tracker knows:
   * which of them reach this board is a setting on the server
   * (VIZADTRAQ_LEAD_TYPES), and a dropdown listing Login and Newsletter above
   * a board that can never contain one is six choices that quietly show
   * nothing. The state pair is fixed, because both always mean something -
   * "show me only the ones who finished the form" is the question this
   * control exists for, and it has to be askable even on a day when
   * everything on the board is partial.
   */
  /* Every capture, not only the one each row is filed under. Somebody who
     booked a demo and used the chatbot is filed under Demo Request, and
     Chatbot has to still be on offer - and to find them. See filterDeals. */
  const capsOf = d => ((d.intel && d.intel.captures && d.intel.captures.length)
    ? d.intel.captures
    : (d.leadType ? [{ type: d.leadType, state: d.leadState }] : []));

  const leadTypes = isBoard
    ? [...new Set(pool.flatMap(d => capsOf(d).map(c => c.type)).filter(Boolean))].sort()
    : [];
  const leadStates = isBoard
    ? [...new Set(pool.flatMap(d => capsOf(d).map(c => c.state)).filter(Boolean))]
      .sort()
      .map(v => [v, leadStateLabel(v) || v])
    : [];

  /* Country and campaign come through leadDetails, which falls back to the
     notes for rows synced before the intel column existed - so the list is
     built the same way the filter matches, and cannot offer a value that
     then matches nothing. */
  const fromIntel = key => (isBoard
    ? [...new Set(pool.map(d => leadDetails(crm, d)[key]).filter(Boolean))].sort()
    : []);

  /* "Manual" is always on offer on the board, rows or no rows: it is the
     source every hand-added row carries, and the filter is how those are
     found. */
  const sources = isBoard
    /* Both of the sources a person can be responsible for, pinned in front
       of the tracker's own — "where did this row come from" is asked of the
       hand-made ones far more often, and on a fresh board neither has any
       rows yet so neither would be offered at all. */
    ? [MANUAL_SOURCE, IMPORT_SOURCE,
      ...seen('source').filter(x => x !== MANUAL_SOURCE && x !== IMPORT_SOURCE)]
    : (isDealish || kind === 'contact') ? seen('source') : (S.sources || []);

  /*
   * Status, kind and source as pills with counts, the way the tracker's own
   * screen puts them.
   *
   * Three dropdowns said the same thing and said it worse: a dropdown hides
   * every option but the chosen one, so "how many chatbot leads are there"
   * meant opening it, reading, and closing it again. These are the three
   * questions anybody asks of this board first, and the answer to all three
   * should be on screen before anything is clicked.
   *
   * The counts are faceted — each one is counted over the rows that pass
   * every OTHER filter, so picking Chatbot leaves the Submitted and Partially
   * Submitted counts telling you what is inside Chatbot rather than what was
   * there before you clicked. A count that does not move when you filter is a
   * count nobody can act on.
   */
  const countBy = (key, value) => (isBoard
    ? filterDeals(crm, { ...cF, [key]: value }, 'pipeline', rows).length
    : 0);

  /*
   * What the row was imported as — "TVC", and whatever else turns up.
   *
   * Only offered where there is one, because it is a label the business
   * adds to some lists and not to others, and a row of chips reading
   * "Segment:" with nothing under it is a control that answers nothing.
   * Read off the rows rather than a settings list: the segments are the
   * ones that have actually been imported, which is the only list that
   * can be right.
   */
  const segments = [...new Set(pool.map(segmentOf).filter(Boolean))].sort();

  const facets = isBoard ? [
    ['Status', 'leadState', leadStates.map(([v, t]) => [v, t])],
    ['Kind', 'leadType', leadTypes.map(v => [v, v])],
    ['Source', 'source', sources.map(v => [v, v])],
    ...(segments.length ? [['Segment', 'segment', segments.map(v => [v, `${v} client`])]] : [])
  ] : [];

  /*
   * One row of pills, any number of them pressed.
   *
   * "Ads and Referral" is a question people actually ask, and a row of
   * pressable pills that only allows one is a radio group wearing the wrong
   * clothes. Pressing a pressed one lets it go, so there is no separate way
   * to clear a choice.
   *
   * The count on each pill stays the count for that value alone, against the
   * other filters — not what the list would become if you added it. "How many
   * came through Referral" has one answer whatever else is selected, and a
   * number that changed depending on your other picks would be unreadable.
   */
  const Facet = ({ name, choices }) => {
    const on = picked(cF[name]);
    const toggle = value => setCF((f) => {
      const now = picked(f[name]);
      const next = now.includes(value) ? now.filter(x => x !== value) : [...now, value];
      return { ...f, [name]: next.join(',') };
    });

    return (
      <div className="ffacet">
        {choices.map(([value, text]) => {
          const n = countBy(name, value);
          const isOn = on.includes(value);
          return (
            <button
              key={value}
              type="button"
              className={`fpill${isOn ? ' on' : ''}${n ? '' : ' none'}`}
              aria-pressed={isOn}
              onClick={() => toggle(value)}
            >
              {text}<em>{n}</em>
            </button>
          );
        })}
      </div>
    );
  };

  /* `values` takes plain strings, or [value, label] pairs where the two differ
     — the origin filter stores 'vizadtraq' but has to read "Demo request". */
  const Dropdown = ({ label, icon, name, values, blank }) => (
    <div className="fld">
      <label><Icon name={icon} />{label}</label>
      <select value={cF[name]} onChange={e => setCF(f => ({ ...f, [name]: e.target.value }))}>
        <option value="">{blank || `All ${label.toLowerCase()}`}</option>
        {values.map(v => {
          const [val, text] = Array.isArray(v) ? v : [v, v];
          return <option key={val} value={val}>{text}</option>;
        })}
      </select>
    </div>
  );

  /*
   * Everybody who could own something here, and how much they do.
   *
   * This listed only the names already on the rows, so a team of fifteen
   * showed as two and the other thirteen looked as though they had been
   * lost. The other way round — the whole team, no counts — is worse: it
   * is a list of choices most of which quietly show nothing.
   *
   * So it is the whole team with the number beside each, in-use first.
   * Picking a zero is then a decision rather than a surprise, and a name
   * that owns nothing is still visibly on the team.
   */
  const owners = (() => {
    const mine = new Map();
    pool.forEach(d => { if (d.owner) mine.set(d.owner, (mine.get(d.owner) || 0) + 1); });

    /* The Owners list, and only it. Names that got onto a row by accident
       — the promote route used to stamp whoever pressed Won — are not
       people to filter a sales board by. */
    return salesTeam(S, crm)
      .sort((a, b) => (mine.get(b) || 0) - (mine.get(a) || 0) || a.localeCompare(b))
      .map(name => [name, `${name} (${mine.get(name) || 0})`]);
  })();

  const setRange = fn => {
    const [a, b] = fn();
    setCF(f => ({ ...f, from: iso(a), to: iso(b) }));
  };

  const activeRange = RANGES.find(([, fn]) => {
    const [a, b] = fn();
    return cF.from === iso(a) && cF.to === iso(b);
  });

  const clearAll = () => setCF(EMPTY_CRM_FILTERS);

  /* What the window measures, said plainly rather than left to be guessed. */
  const dateLabel = kind === 'activity' ? 'Due' : 'Added';

  return (
    <>
      <div className="fbar">
        <div className="fspace" />
        {tools}
        {active ? (
          <button type="button" className="btn" onClick={clearAll}>
            <Icon name="refresh" />Clear
          </button>
        ) : null}
        <button
          type="button"
          className={`btn${active ? ' primary' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <Icon name="filter" />Filters
          {active ? <span className="fcount">{active}</span> : null}
          <span className={`fchev${open ? ' up' : ''}`}><Icon name="chev" /></span>
        </button>
      </div>

      {open ? (
      <Card className="fpanel">
      {/* The three questions this board is asked first, answered before
          anything is clicked. See the note beside facets above. */}
      {facets.length ? (
        <div className="ffacets">
          {facets.map(([title, name, choices]) => (choices.length ? (
            <div key={name} className="ffrow">
              <span className="fflabel">{title}</span>
              <Facet name={name} choices={choices} />
            </div>
          ) : null))}
        </div>
      ) : null}

      <div className="fgrid">
        <div className="fld">
          <label><Icon name="search" />Search</label>
          <div className="srch">
            <Icon name="search" />
            <input
              placeholder={isDealish ? 'Name, company, notes…' : 'Search…'}
              value={cF.q}
              onChange={e => setCF(f => ({ ...f, q: e.target.value }))}
            />
            {cF.q ? (
              <button
                type="button"
                className="iconbtn sm"
                aria-label="Clear the search"
                onClick={() => setCF(f => ({ ...f, q: '' }))}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </div>
        </div>

        {/* A lead climbs its own short ladder; a deal climbs the long one; an
            activity is only open or done. Three different questions wearing
            one control, so each page gets the list that belongs to it. */}
        {/* The board has no Won - a row that reaches it has left for Deals -
            and the Deals page has no stage at all, since everything on it
            is won. */}
        {/* The board's stage is chosen on the stage tiles and the sub-status
            pills above the table (brief §6), so no Stage dropdown here. */}
        {isBoard || kind === 'deal' ? null
          : kind === 'activity'
            ? <Dropdown label="State" icon="flag" name="status" values={ACT_STATES} blank="Open and done" />
            : kind === 'contact'
              ? <Dropdown label="Status" icon="flag" name="status" values={seen('status')} />
              : <Dropdown label="Status" icon="flag" name="status" values={S.lStatus || []} />}


        {/* Source, Enquiry and Form are the pills above; a second copy as a
            dropdown is two controls for one filter, and they would disagree
            the first time either changed. */}
        {kind === 'activity' || isBoard ? null
          : <Dropdown label="Source" icon="zap" name="source" values={sources} />}

        {/* And the label it travels under. A dropdown here rather than a
            pill because the deal list has no pill strip; on the board it is
            a pill with the rest. Only where a segment exists at all. */}
        {kind === 'deal' && segments.length
          ? <Dropdown label="Segment" icon="tag" name="segment" values={segments} blank="Any segment" />
          : null}

        {/* Already a customer, or still to sell to.
            The board carries registrations, and a registration that went
            through is somebody with an account - 263 of 273 of them are in
            the clients book. They belong on the list, but "who is left to
            sell to" is where a morning of calls starts. */}
        {isBoard
          ? <Dropdown label="Customer" icon="users" name="customer" blank="Client or not"
              values={[["prospect", "Not a client yet"], ["client", "Already a client"]]} />
          : null}

        {owners.length
          ? <Dropdown label="Owner" icon="users" name="owner" values={owners} blank="Anyone" />
          : null}

        {kind === 'deal'
          ? <Dropdown label="Plan" icon="tag" name="plan"
              values={[...new Set([...(S.plans || []), ...seen('plan')])]} blank="Any plan" />
          : null}

        {kind === 'company'
          ? <Dropdown label="Industry" icon="building" name="industry" values={seen('industry')} />
          : null}

        {/* What brought them. The place they were in is on every row already
            and nobody narrowed by it; the campaign is the one somebody asks
            about, because it is the one with a budget behind it. */}
        {(() => {
          const campaigns = fromIntel('campaign');
          return campaigns.length
            ? <Dropdown label="Campaign" icon="tag" name="campaign" values={campaigns}
                blank="Any campaign" />
            : null;
        })()}
      </div>

      {/* The window, with its own row: two dates and the handful of ranges
          worth a click. Each end clears on its own, because a native date
          input has no way back to empty once a date is in it. */}
      <div className="fdates">
        <div className="fld">
          <label>
            <Icon name="cal" />{dateLabel} from
            {cF.from ? (
              <button
                type="button"
                className="fclear"
                aria-label="Clear the start date"
                onClick={() => setCF(f => ({ ...f, from: '' }))}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </label>
          <input
            type="date"
            value={cF.from}
            max={cF.to || undefined}
            onChange={e => setCF(f => ({ ...f, from: e.target.value }))}
          />
        </div>

        <div className="fld">
          <label>
            <Icon name="cal" />{dateLabel} to
            {cF.to ? (
              <button
                type="button"
                className="fclear"
                aria-label="Clear the end date"
                onClick={() => setCF(f => ({ ...f, to: '' }))}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </label>
          <input
            type="date"
            value={cF.to}
            min={cF.from || undefined}
            onChange={e => setCF(f => ({ ...f, to: e.target.value }))}
          />
        </div>

        <div className="fld span2">
          <label><Icon name="zap" />Quick range</label>
          <div className="franges">
            {RANGES.map(([name, fn]) => (
              <button
                key={name}
                type="button"
                className={`frange${activeRange && activeRange[0] === name ? ' on' : ''}`}
                onClick={() => setRange(fn)}
              >
                {name}
              </button>
            ))}
            {cF.from || cF.to ? (
              <button
                type="button"
                className="frange clear"
                onClick={() => setCF(f => ({ ...f, from: '', to: '' }))}
              >
                <Icon name="x" />Clear dates
              </button>
            ) : null}
          </div>
        </div>
      </div>
      </Card>
      ) : null}
    </>
  );
}
